import React from 'react';
import ReactDOM from 'react-dom';
import { data } from 'hudl-base';
import { getConsentState } from '../../utility/consent-utils';
import shouldUseMobileAd from '../../utility/ads/should-use-mobile-ad';
import './display-ad.scss';

const GOOGLE_AD_REFRESH_INTERVAL = 15000; // 15 seconds in milliseconds
const REFRESH_TIMER = 5000; // 5 seconds in milliseconds

class DisplayAd extends React.Component {
    static defaultProps = {
        isMobile: false,
        disableAdWrapper: false,
        variant: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            isAdBlocked: false,
            lastAdRefreshTime: null,
            isActive: this.computeIsActive(props),
        };

        this.refreshTimer = null;
        this.refreshPaused = false;
        this.adContainerRef = null;
    }

    componentDidMount() {
        this.detectAdBlock();
        if (this.state.isActive) {
            this.displayAdWhenReady();
        } else {
            // Still poll so a viewport change can pick up later.
            this.startRefreshTimer();
            this.addFocusAndBlurListeners();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.id !== this.props.id) {
            this.displayAdWhenReady();
            return;
        }
        if (this.state.isActive && !prevState.isActive) {
            // Viewport just brought this variant on — define + refresh the slot
            // now that its DOM container is rendered.
            this.displayAdWhenReady();
        } else if (!this.state.isActive && prevState.isActive) {
            // Destroy the slot so it disappears from
            // `googletag.pubads().getSlots()`. Otherwise other consumers — most
            // notably the `GoogleAdRefresher` window-resize listener — will
            // refresh slots for variants that aren't currently rendered, firing
            // extra GAM calls. CBUPT-51359.
            const slot = this.getAdSlotByDomId(this.props.id);
            if (slot) {
                window.googletag.destroySlots([slot]);
            }
        }
    }

    // True when this variant matches the current viewport width. Variants
    // without a declared `variant` prop are always active (legacy callers).
    computeIsActive = (props = this.props) => {
        if (!props.variant) {
            return true;
        }
        const isNarrow = shouldUseMobileAd();
        return props.variant === 'mobile' ? isNarrow : !isNarrow;
    };

    componentWillUnmount() {
        this.stopRefreshTimer();
        this.removeFocusAndBlurListeners();
    }

    getAdSlotByDomId = (domId) => {
        const slots = window.googletag.pubads().getSlots();
        const foundSlot = slots.find(slot => slot.getSlotId().getDomId() === domId);
        return foundSlot;
    };

    detectAdBlock = () => {
        fetch('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js', {
            method: 'HEAD',
            mode: 'no-cors',
        }).catch(() => {
            this.setState({ isAdBlocked: true });
        });
    };

    getPercentageVisible = () => {
        const node = ReactDOM.findDOMNode(this.adContainerRef);
        const rect = node.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        let visibility = 0;
        if (rect.bottom > windowHeight) {
            visibility = (windowHeight - rect.top) / rect.height;
        } else if (rect.top < 0) {
            visibility = rect.bottom / rect.height;
        } else {
            visibility = 1; // Fully visible
        }
        return (visibility * 100).toFixed(0);
    };

    startRefreshTimer = () => {
        if (this.refreshTimer) {
            return;
        }
        this.refreshTimer = setInterval(() => {
            // Re-evaluate viewport-based variant selection on each tick. If the
            // active variant flipped, update state and let componentDidUpdate
            // handle (un)mounting the DOM container + firing the new request.
            // CBUPT-51359.
            const isActive = this.computeIsActive();
            if (isActive !== this.state.isActive) {
                this.setState({ isActive });
                return;
            }
            if (!isActive) {
                return;
            }

            const percentageVisible = this.getPercentageVisible();
            const currentTime = Date.now();
            const lastRefreshTime = this.state.lastAdRefreshTime;
            const timeSinceLastRefresh = lastRefreshTime ? (currentTime - lastRefreshTime) : null;

            if (percentageVisible >= 51 && !this.refreshPaused && timeSinceLastRefresh >= GOOGLE_AD_REFRESH_INTERVAL) {
                googletag.cmd.push(() => {
                    this.refreshAdSlot();
                });
            }
        }, REFRESH_TIMER);
    };

    stopRefreshTimer = () => {
        clearInterval(this.refreshTimer);
    };

    refreshAdSlot = () => {
        let slot = this.getAdSlotByDomId(this.props.id);
        if (!slot) {
            slot = this.createSlot(this.props.id);
            googletag.display(this.props.id);
        }
        window.googletag.pubads().refresh([slot]);
        this.setState({ lastAdRefreshTime: Date.now() });
    };

    addFocusAndBlurListeners = () => {
        window.addEventListener('focus', this.resumeRefreshTimer);
        window.addEventListener('blur', this.pauseRefreshTimer);
    };

    removeFocusAndBlurListeners = () => {
        window.removeEventListener('focus', this.resumeRefreshTimer);
        window.removeEventListener('blur', this.pauseRefreshTimer);
    };

    pauseRefreshTimer = () => {
        this.refreshPaused = true;
    };

    resumeRefreshTimer = () => {
        this.refreshPaused = false;
    };

    displayAdWhenReady = () => {
        getConsentState().then((consentState) => {
            googletag.cmd.push(() => {
                this.refreshAdSlot();
                this.startRefreshTimer();
                this.addFocusAndBlurListeners();
            });
        });
    };

    createSlot = (adId) => {
        const googletag = window.googletag;
        const adData = data.get('ads');
        if (adData.targetingData?.nonPersonalizedAds == "True") {
            googletag.pubads().setPrivacySettings({nonPersonalizedAds: true, restrictDataProcessing: true});
        }

        const adPath = `/${adData.networkId}/${this.props.path}`;
        let slot = googletag.defineSlot(adPath, this.props.sizes, adId).setCollapseEmptyDiv(true);

        Object.keys(adData.targetingData).forEach(key => {
            if (key === 'nonPersonalizedAds') {
                return;
            }

            if (key === 'adContext' && adData.targetingData[key]) {
                adData.targetingData[key].split('&').forEach(pair => {
                    const [ctxKey, ctxValue] = pair.split('=');
                    if (ctxKey && ctxValue) {
                        slot = slot.setTargeting(ctxKey, ctxValue);
                    }
                });
            } else if (adData.targetingData[key] !== null && adData.targetingData[key] !== undefined) {
                slot = slot.setTargeting(key, adData.targetingData[key]);
            }
        });

        if (this.props.adSizeMapping) {
            let mapping = googletag.sizeMapping();
            this.props.adSizeMapping.forEach(value => {
                mapping = mapping.addSize(value.browser, value.ad);
            });
            slot = slot.defineSizeMapping(mapping.build());
        }

        slot = slot.addService(googletag.pubads());
        return slot;
    };


    render() {
        const { id, isMobile, disableAdWrapper } = this.props;
        const { isAdBlocked, isActive } = this.state;

        // Variant doesn't match the current viewport — render nothing so the
        // ad slot stays out of the DOM and the polling timer can't refresh it.
        // CBUPT-51359.
        if (!isActive) {
            return null;
        }

        return disableAdWrapper || isAdBlocked ? (
            <div id={id} ref={el => (this.adContainerRef = el)} />
        ) : (
            <div className={isMobile ? 'mobile-ad-container' : ''}>
                <div id={id} ref={el => (this.adContainerRef = el)} style={!isMobile ? { width: 300, height: 250 } : {}} />
            </div>
        );
    }
}

export default DisplayAd;
